'use client';

import { BrandLogo } from '@/components/BrandLogo';
import Link from 'next/link';
import { type FormEvent, useState } from 'react';

/**
 * 2026-07-06 — password-forgot form. Same visual shell as PasswordLoginForm
 * so the switch from 密码登录 → 忘记密码 feels contiguous.
 *
 * We always show the same success state regardless of whether the email
 * exists — the orchestrator returns 200 either way to avoid enumeration.
 */
export function ForgotForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/auth/password/forgot', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setError('请求太频繁，请稍后再试');
        } else {
          setError('发送失败，请稍后重试');
        }
        setPending(false);
        return;
      }
      setSent(true);
      setPending(false);
    } catch {
      setError('网络错误，请重试');
      setPending(false);
    }
  }

  return (
    <main className="page-in max-w-md mx-auto px-6 pt-20 pb-16">
      <div className="flex justify-center mb-10">
        <Link href="/" aria-label="ChiefEco AI" className="inline-flex">
          <BrandLogo variant="vertical" heightPx={140} />
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-soft p-8 shadow-sm">
        {sent ? (
          <>
            <h1 className="font-serif text-2xl text-ink mb-2">重置邮件已发送</h1>
            <p className="text-dusk text-sm mb-4 leading-relaxed">
              如果 <strong>{email}</strong> 已注册，我们已把重置链接发到你的邮箱。 链接 30
              分钟内有效。
            </p>
            <p className="text-dusk text-sm mb-6 leading-relaxed">
              没收到邮件？检查一下垃圾邮件文件夹，或者{' '}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-warmth hover:underline bg-transparent border-0 p-0 cursor-pointer"
              >
                重新发送
              </button>
              。
            </p>
            <Link
              href="/login"
              className="block text-center bg-warmth-dark text-white font-medium px-4 py-3 rounded-2xl hover:bg-warmth transition min-h-touch"
            >
              返回登录
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-serif text-2xl text-ink mb-2">忘记密码?</h1>
            <p className="text-dusk text-sm mb-6 leading-relaxed">
              输入你注册时用的邮箱，我们会发一封重置链接过去，链接 30 分钟内有效。
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="block text-sm text-ink mb-1.5 font-medium">
                  邮箱
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-soft bg-cream/50 text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition"
                  placeholder="you@example.com"
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="text-sm text-warmth bg-warmth/10 border border-warmth/20 rounded-2xl px-4 py-3"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={pending}
                className="w-full bg-warmth-dark text-white font-medium px-4 py-3 rounded-2xl hover:bg-warmth transition disabled:opacity-60 disabled:cursor-not-allowed min-h-touch"
              >
                {pending ? '发送中…' : '发送重置链接'}
              </button>
            </form>

            <p className="text-center text-sm text-dusk mt-6">
              <Link href="/login/password" className="text-warmth hover:underline">
                想起来了 — 返回密码登录
              </Link>
            </p>
            <p className="text-center text-sm text-dusk mt-2">
              <Link href="/login" className="text-warmth hover:underline">
                用验证码登录
              </Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
